// Port of Weather/WeatherCodes.swift: WMO codes, CAPE-based thunderstorm risk, MET Norway symbol codes and MET's
// thunder-probability risk model.

import type { ThunderstormRisk, WeatherCondition } from '../core/models';
import { compareThunderstormRisk } from '../core/domain';

/** WMO present-weather codes (table 4677, as used by Open-Meteo's `weather_code`). */
export const WMOWeatherCode = {
  /** Codes meaning a thunderstorm is forecast (95 slight/moderate, 96/99 with hail). */
  thunderstormCodes: new Set([95, 96, 99]) as ReadonlySet<number>,
  /** Showery precipitation codes — convective activity even without thunder. */
  showerCodes: new Set([80, 81, 82, 85, 86]) as ReadonlySet<number>,

  /** Maps a WMO code to TrailBivvy's coarse condition. Unrecognised codes map to `unknown`. */
  condition(code: number): WeatherCondition {
    switch (code) {
      case 0:
      case 1:
        return 'clear';
      case 2:
        return 'partlyCloudy';
      case 3:
        return 'cloudy';
      case 45:
      case 48:
        return 'fog';
      case 51:
      case 53:
      case 55:
        return 'drizzle';
      case 56:
      case 57:
      case 66:
      case 67:
        return 'sleet'; // freezing drizzle / freezing rain: icy precipitation
      case 61:
      case 63:
      case 65:
        return 'rain';
      case 71:
      case 73:
      case 75:
      case 77:
        return 'snow';
      case 80:
      case 81:
      case 82:
        return 'showers';
      case 85:
      case 86:
        return 'snow'; // snow showers
      case 95:
      case 96:
      case 99:
        return 'thunderstorm';
      default:
        return 'unknown';
    }
  },

  isThunderstorm(code: number): boolean {
    return WMOWeatherCode.thunderstormCodes.has(code);
  },
};

function finite(value: number | undefined): number | undefined {
  return value !== undefined && Number.isFinite(value) ? value : undefined;
}

/**
 * Hourly thunderstorm risk from the model's weather code, convective instability (CAPE) and precipitation.
 * 1. A thunderstorm weather code (95/96/99) → high, whatever the other inputs.
 * 2. Otherwise CAPE is required; missing or invalid CAPE → unknown (never silently none).
 * 3. CAPE < 800 J/kg → none; ≥ 800 → low; ≥ 1500 → moderate; ≥ 2500 → high.
 * 4. When precipitation data is known and shows no signal (probability < 30 %, amount < 0.2 mm, no shower code) the
 *    CAPE level is lowered by one step, but never below low.
 */
export const ThunderstormRiskModel = {
  lowCAPE: 800,
  moderateCAPE: 1500,
  highCAPE: 2500,
  /** Precipitation probability (0…1) from which convection is considered triggered. */
  precipitationProbabilitySignal: 0.3,
  /** Hourly precipitation (mm) from which convection is considered triggered. */
  precipitationAmountSignal: 0.2,

  risk(
    weatherCode: number | undefined,
    cape: number | undefined,
    precipitationProbability: number | undefined,
    precipitation: number | undefined,
  ): ThunderstormRisk {
    if (weatherCode !== undefined && WMOWeatherCode.isThunderstorm(weatherCode)) return 'high';
    if (cape === undefined || !Number.isFinite(cape) || cape < 0) return 'unknown';
    let base: ThunderstormRisk;
    if (cape >= ThunderstormRiskModel.highCAPE) base = 'high';
    else if (cape >= ThunderstormRiskModel.moderateCAPE) base = 'moderate';
    else if (cape >= ThunderstormRiskModel.lowCAPE) base = 'low';
    else return 'none';
    // Unknown precipitation keeps the CAPE level: be conservative.
    const triggered = ThunderstormRiskModel.precipitationSignal(weatherCode, precipitationProbability, precipitation);
    if (triggered !== false) return base;
    return base === 'high' ? 'moderate' : 'low';
  },

  /** Whether the precipitation fields show convection; undefined when there is no precipitation information at all. */
  precipitationSignal(
    weatherCode: number | undefined,
    probability: number | undefined,
    amount: number | undefined,
  ): boolean | undefined {
    const p = finite(probability);
    const a = finite(amount);
    if (p === undefined && a === undefined && weatherCode === undefined) return undefined;
    if (weatherCode !== undefined && WMOWeatherCode.showerCodes.has(weatherCode)) return true;
    if (p !== undefined && p >= ThunderstormRiskModel.precipitationProbabilitySignal) return true;
    if (a !== undefined && a >= ThunderstormRiskModel.precipitationAmountSignal) return true;
    return false;
  },

  /** Daily risk: the most severe hourly risk (unknown ranks above none). Empty input → unknown. */
  dailyRisk(hourly: Iterable<ThunderstormRisk>): ThunderstormRisk {
    let worst: ThunderstormRisk | undefined;
    for (const risk of hourly) if (worst === undefined || compareThunderstormRisk(risk, worst) > 0) worst = risk;
    return worst ?? 'unknown';
  },
};

/**
 * MET Norway weather symbol codes (`symbol_code`, e.g. `partlycloudy_day`, `lightrainshowersandthunder_night`; legend
 * at github.com/metno/weathericons). Matching uses substrings so MET's historical spellings
 * (`lightssleetshowersandthunder`) map too.
 */
export const METSymbolCode = {
  /** The code without its `_day` / `_night` / `_polartwilight` suffix, lower-cased. */
  base(code: string): string {
    const lowered = code.trim().toLowerCase();
    // Swift `split(separator: "_", maxSplits: 1).first` skips empty pieces: leading underscores are ignored.
    const stripped = lowered.replace(/^_+/, '');
    const index = stripped.indexOf('_');
    return index === -1 ? stripped : stripped.slice(0, index);
  },

  /** Whether MET forecasts thunder with this symbol. */
  hasThunder(code: string): boolean {
    return METSymbolCode.base(code).includes('thunder');
  },

  /**
   * Maps a symbol code to a coarse condition. Thunder wins over the precipitation type; sleet wins over snow and
   * rain; snow showers are snow and rain showers showers; MET has no drizzle symbol; `fair` is clear.
   */
  condition(code: string): WeatherCondition {
    const name = METSymbolCode.base(code);
    if (name.includes('thunder')) return 'thunderstorm';
    if (name.includes('sleet')) return 'sleet';
    if (name.includes('snow')) return 'snow';
    if (name.includes('rainshowers')) return 'showers';
    if (name.includes('rain')) return 'rain';
    switch (name) {
      case 'clearsky':
      case 'fair':
        return 'clear';
      case 'partlycloudy':
        return 'partlyCloudy';
      case 'cloudy':
        return 'cloudy';
      case 'fog':
        return 'fog';
      default:
        return 'unknown';
    }
  },
};

/**
 * Thunderstorm risk from MET Norway data, which carries no CAPE.
 * 1. A symbol containing `thunder` → high.
 * 2. Otherwise, with `probability_of_thunder`: < 5 % → none; ≥ 5 % → low; ≥ 15 % → moderate; ≥ 30 % → high
 *    (TrailBivvy's own cautious thresholds, not MET categories).
 * 3. Otherwise → unknown: a symbol without thunder is MET's most likely weather, not an assessment of the risk.
 */
export const METThunderRiskModel = {
  lowProbability: 0.05,
  moderateProbability: 0.15,
  highProbability: 0.3,

  /** `thunderProbability` is 0…1 (undefined when MET does not give it). */
  risk(symbolCode: string | undefined, thunderProbability: number | undefined): ThunderstormRisk {
    if (symbolCode !== undefined && METSymbolCode.hasThunder(symbolCode)) return 'high';
    const p = thunderProbability;
    if (p === undefined || !Number.isFinite(p) || p < 0) return 'unknown';
    if (p >= METThunderRiskModel.highProbability) return 'high';
    if (p >= METThunderRiskModel.moderateProbability) return 'moderate';
    if (p >= METThunderRiskModel.lowProbability) return 'low';
    return 'none';
  },
};
